/**
 * @fileoverview Phone input strings barrel — the EN/ES bundles plus
 *   `loadPhoneUiStrings(i18nInstance, locales)`, which registers them under the `phoneUi` namespace on a
 *   react-i18next instance.
 * @package @puffin-core/phone-ui
 *
 * ## 🔴 LEAF-ONLY by design — this subpath must not import a component
 *
 * `@puffin-core/i18n-namespaces` imports this module at APP BOOT from every web app AND every marketing
 * portal, so anything reachable from here lands in every boot chunk. It imports only its own frozen data
 * modules, a zero-import namespace constant, and the React-free strings-kit resolver.
 *
 * @module @puffin-core/phone-ui/strings
 */

import { PHONE_UI_EN } from './en.js';
import { PHONE_UI_ES } from './es.js';
import { PHONE_UI_NAMESPACE } from './namespace.js';

export { PHONE_UI_EN, PHONE_UI_ES, PHONE_UI_NAMESPACE };

const BUNDLES = Object.freeze({ en: PHONE_UI_EN, es: PHONE_UI_ES });

/**
 * The locales this package ships bundles for — DERIVED from `BUNDLES`, never typed out
 * (ENUM-DRIFT-001).
 *
 * @constant {ReadonlyArray<string>}
 */
export const SUPPORTED_LOCALES = Object.freeze(Object.keys(BUNDLES));

/**
 * Register one or more phone input bundles on a react-i18next instance.
 *
 * @param {object} i18nInstance - The react-i18next instance.
 * @param {string|string[]} [locales='en'] - Locale code(s) to load.
 * @returns {object} The same instance, for chaining.
 * @throws {Error} If the instance lacks `addResourceBundle`, or a locale is unsupported.
 */
export function loadPhoneUiStrings(i18nInstance, locales = 'en') {
  if (!i18nInstance || typeof i18nInstance.addResourceBundle !== 'function') {
    throw new Error('loadPhoneUiStrings: expected a react-i18next instance with addResourceBundle()');
  }
  const list = Array.isArray(locales) ? locales : [locales];
  list.forEach((locale) => {
    const bundle = BUNDLES[locale];
    if (!bundle) {
      throw new Error(
        `loadPhoneUiStrings: unsupported locale "${locale}". Supported: ${SUPPORTED_LOCALES.join(', ')}`,
      );
    }
    // Deep merge so an app may add keys; never overwrite, so a caller-defined value wins.
    i18nInstance.addResourceBundle(locale, PHONE_UI_NAMESPACE, bundle, true, false);
  });
  return i18nInstance;
}

export { resolveString } from './resolveString.js';
